import React, { useEffect, useState } from "react";
import {
    Alert,
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import { getDispatchHistry } from "../../services/getDispatchHistry";
import { getInspectionEntry } from "../../services/getInspectionEntry";
import { getproductionplanstatusByPO } from "../../services/getProductionPlanStatus";

const formatDate = (date) => {
    if (!date) return "-";

    const parsedDate = new Date(date);

    if (Number.isNaN(parsedDate.getTime())) {
        return "-";
    }

    return parsedDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const thStyle = {
    padding: "7px 10px",
    borderBottom: "1px solid #ddd",
    borderRight: "1px solid #eee",
    textAlign: "center",
    fontWeight: 600,
    whiteSpace: "nowrap",
    backgroundColor: "#f5f5f5",
};

const tdStyle = {
    padding: "7px 10px",
    borderBottom: "1px solid #eee",
    borderRight: "1px solid #eee",
    textAlign: "center",
    whiteSpace: "nowrap",
};

const ProductionPlanDetailsDialog = ({
    open,
    onClose,
    poId,
}) => {
    const [dispatchLoading, setDispatchLoading] = useState(false);
    const [inspectionLoading, setInspectionLoading] = useState(false);

    const [dispatchError, setDispatchError] = useState("");
    const [inspectionError, setInspectionError] = useState("");

    const [dispatchData, setDispatchData] = useState(null);
    const [inspectionData, setInspectionData] = useState(null);

    const [planStatusLoading, setPlanStatusLoading] = useState(false);
    const [planStatusError, setPlanStatusError] = useState("");
    const [planStatusData, setPlanStatusData] = useState(null);



    useEffect(() => {
        if (!open || !poId) {
            return;
        }

        const loadDetails = async () => {
            setDispatchLoading(true);
            setInspectionLoading(true);

            setDispatchError("");
            setInspectionError("");

            setDispatchData(null);
            setInspectionData(null);

            setPlanStatusLoading(true);
            setPlanStatusError("");
            setPlanStatusData(null);


        // -----------------------------------------
        // Production Plan Status
        // -----------------------------------------
        try {
            const result = await getproductionplanstatusByPO(poId);

            setPlanStatusData(result.data);
        } catch (error) {
            console.error(
                "Production Plan Status Error:",
                error
            );

            setPlanStatusError(
                error.response?.data?.message ||
                error.response?.data ||
                error.message ||
                "Unable to load production plan status."
            );
        } finally {
            setPlanStatusLoading(false);
        }
            // -----------------------------------------
            // Dispatch History
            // -----------------------------------------
            try {
                const result = await getDispatchHistry(poId);

                setDispatchData(result);
            } catch (error) {
                console.error(
                    "Dispatch History Error:",
                    error
                );

                setDispatchError(
                    error.response?.data?.message ||
                        error.response?.data ||
                        error.message ||
                        "Unable to load dispatch history."
                );
            } finally {
                setDispatchLoading(false);
            }

            // -----------------------------------------
            // Inspection Entry
            // -----------------------------------------
            try {
                const result = await getInspectionEntry(poId);

                setInspectionData(result);
            } catch (error) {
                console.error(
                    "Inspection Entry Error:",
                    error
                );

                setInspectionError(
                    error.response?.data?.message ||
                        error.response?.data ||
                        error.message ||
                        "Unable to load inspection entry."
                );
            } finally {
                setInspectionLoading(false);
            }
        };

        loadDetails();
    }, [open, poId]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="lg"
        >
            {/* HEADER */}
            <DialogTitle
                sx={{
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    backgroundColor: "#f8fafc",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            fontWeight={700}
                            color="primary"
                        >
                        Production Plan Details
                        </Typography>

                        <Typography
                            variant="body2"
                            color="text.secondary"
                        >
                            POrder ID:{" "}
                            <strong>{poId || "-"}</strong>
                        </Typography>
                    </Box>

                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    p: 2,
                    backgroundColor: "#f5f7fa",
                }}
            >

                <Box
    sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        border: "1px solid #e0e0e0",
        mb: 2,
        overflow: "hidden",
    }}
>
    <Box
        sx={{
            display: "flex",
            alignItems: "center",
            px: 2,
            py: 1,
            backgroundColor: "#f8fafc",
            borderBottom: "1px solid #e0e0e0",
        }}
    >
        <Typography fontWeight={700}>
            Plan Status
        </Typography>
    </Box>

    <Box sx={{ p: 2 }}>
        {planStatusLoading && (
            <CircularProgress size={28} />
        )}

        {planStatusError && !planStatusLoading && (
            <Alert severity="error">
                {planStatusError}
            </Alert>
        )}

        {planStatusData?.data?.length > 0 && (
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                }}
            >
                <thead>
                    <tr>
                        <th style={thStyle}>PO Number</th>
                        <th style={thStyle}>Plan Month</th>
                        <th style={thStyle}>Year</th>
                        <th style={thStyle}>Produced Qty</th>
                        <th style={thStyle}>Pending Qty</th>
                        <th style={thStyle}>Total Planned Qty</th>
                    </tr>
                </thead>

                <tbody>
                    {planStatusData.data.map(
                        (row, index) => (
                            <tr key={index}>
                                <td style={tdStyle}>
                                    {row.po_number}
                                </td>

                                <td style={tdStyle}>
                                    {row.plan_month}
                                </td>

                                <td style={tdStyle}>
                                    {row.year}
                                </td>

                                <td style={tdStyle}>
                                    {row.qty_produced}
                                </td>

                                <td style={tdStyle}>
                                    {row.qty_pending}
                                </td>

                                <td style={tdStyle}>
                                    {row.total_planed_qty}
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        )}

        {!planStatusLoading &&
            !planStatusError &&
            (!planStatusData?.data ||
                planStatusData.data.length === 0) && (
                <Alert severity="info">
                    No production plan status found.
                </Alert>
            )}
    </Box>
</Box>
                 {/* =====================================================
                    INSPECTION ENTRY
                ===================================================== */}
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            backgroundColor: "#f8fafc",
                            borderBottom:
                                "1px solid #e0e0e0",
                        }}
                    >
                        <FactCheckIcon
                            color="primary"
                            fontSize="small"
                        />

                        <Typography
                            fontWeight={700}
                        >
                            Inspection Detail
                        </Typography>
                    </Box>

                    <Box sx={{ p: 1 }}>
                        {inspectionLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    py: 3,
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {inspectionError &&
                            !inspectionLoading && (
                                <Alert severity="error">
                                    {inspectionError}
                                </Alert>
                            )}

                        {inspectionData &&
                            !inspectionLoading &&
                            !inspectionError && (
                                inspectionData?.data
                                    ?.length > 0 ? (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            overflowX:
                                                "auto",
                                        }}
                                    >
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse:
                                                    "collapse",
                                                fontSize:
                                                    "13px",
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        #
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        PO Number
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Inspection Call
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Insp. Qty
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Passed Qty
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Certificate
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Remarks
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {inspectionData.data.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => (
                                                        <tr
                                                            key={
                                                                row.recId ||
                                                                index
                                                            }
                                                        >
                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {index +
                                                                    1}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.poNumber ||
                                                                    "-"}
                                                            </td>

                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {formatDate(
                                                                    row.insp_Call_Date
                                                                )}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.inspQty ??
                                                                    0}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.passedQty ??
                                                                    0}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    whiteSpace:
                                                                        "normal",
                                                                    minWidth:
                                                                        "220px",
                                                                }}
                                                            >
                                                                {row.inspCertificate ||
                                                                    "-"}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    whiteSpace:
                                                                        "normal",
                                                                }}
                                                            >
                                                                {row.remarks ||
                                                                    "-"}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        No inspection entry
                                        found for this PO.
                                    </Alert>
                                )
                            )}
                    </Box>
                </Box>
                {/* =====================================================
                    DISPATCH HISTORY
                ===================================================== */}
                <Box
                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,
                        border: "1px solid #e0e0e0",
                        mb: 2,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            px: 2,
                            py: 1,
                            backgroundColor: "#f8fafc",
                            borderBottom:
                                "1px solid #e0e0e0",
                        }}
                    >
                        <LocalShippingIcon
                            color="primary"
                            fontSize="small"
                        />

                        <Typography
                            fontWeight={700}
                        >
                            Dispatch Detail
                        </Typography>
                    </Box>

                    <Box sx={{ p: 1 }}>
                        {dispatchLoading && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "center",
                                    py: 3,
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        )}

                        {dispatchError &&
                            !dispatchLoading && (
                                <Alert severity="error">
                                    {dispatchError}
                                </Alert>
                            )}

                        {dispatchData &&
                            !dispatchLoading &&
                            !dispatchError && (
                                dispatchData?.data
                                    ?.length > 0 ? (
                                    <Box
                                        sx={{
                                            width: "100%",
                                            overflowX:
                                                "auto",
                                        }}
                                    >
                                        <table
                                            style={{
                                                width: "100%",
                                                borderCollapse:
                                                    "collapse",
                                                fontSize:
                                                    "13px",
                                            }}
                                        >
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        #
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Invoice No
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Dispatch Date
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Item
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Ordered Qty
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Dispatch Qty
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Pending Qty
                                                    </th>

                                                    <th
                                                        style={
                                                            thStyle
                                                        }
                                                    >
                                                        Running Dispatch
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                {dispatchData.data.map(
                                                    (
                                                        row,
                                                        index
                                                    ) => (
                                                        <tr
                                                            key={
                                                                row.saleRecId ||
                                                                index
                                                            }
                                                        >
                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {index +
                                                                    1}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.invoiceNo ||
                                                                    "-"}
                                                            </td>

                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {formatDate(
                                                                    row.invoiceDate
                                                                )}
                                                            </td>

                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {row.productName ||
                                                                    "-"}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.orderedQty ??
                                                                    0}
                                                            </td>

                                                            <td
                                                                style={{
                                                                    ...tdStyle,
                                                                    fontWeight: 600,
                                                                }}
                                                            >
                                                                {row.dispatchQty ??
                                                                    0}
                                                            </td>

                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {row.pendingQty ??
                                                                    0}
                                                            </td>

                                                            <td
                                                                style={
                                                                    tdStyle
                                                                }
                                                            >
                                                                {row.runningDispatch ??
                                                                    "-"}
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </Box>
                                ) : (
                                    <Alert severity="info">
                                        No dispatch history
                                        found for this PO.
                                    </Alert>
                                )
                            )}
                    </Box>
                </Box>

               
            </DialogContent>
        </Dialog>
    );
};

export default ProductionPlanDetailsDialog;